/**
 * Cobros de Hoy — widget del Inicio: clientes con cuota que vence hoy.
 *
 * El Inicio lo monta para usuarios con permisos de cobro (mismas keys que el
 * acceso rápido "Cobrar Pagos"). El alcance de sucursal lo decide el backend
 * (/api/cobrosdeldia vía _perfil_reporte): un usuario normal ve su sucursal;
 * admin o rol con `versucursales` ve todas (y cada fila indica la sucursal).
 *
 * Clic en un cliente abre el expediente del préstamo. La lista completa se
 * expande DENTRO del widget (Cobrar Pagos es la pantalla de cobrar, no
 * lista los vencimientos del día; el pie la ofrece como acción aparte).
 * Con datos en cero muestra estado vacío (igual que Mi Gestión de Cobros:
 * visible siempre que haya permiso).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Typography from "@mui/material/Typography";
import { useApi } from "lib/api";
import { FP } from "components/financepro/tokens";

const MAX_CLIENTES = 6;

const money = (v) =>
  Number(v || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Stat({ valor, etiqueta }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", color: FP.color.textPrimary }}>
        {valor}
      </Typography>
      <Typography sx={{ fontSize: "0.74rem", color: FP.color.textSecondary }}>{etiqueta}</Typography>
    </Box>
  );
}

function CobrosDeHoy() {
  const navigate = useNavigate();
  const { post } = useApi();
  const [datos, setDatos] = useState(null);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    let activo = true;
    post("/api/cobrosdeldia").then(({ ok, data }) => {
      if (activo && ok) setDatos(data);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!datos) return null;

  const clientes = datos.clientes || [];
  const visibles = expandido ? clientes : clientes.slice(0, MAX_CLIENTES);
  const cuotas = clientes.reduce((s, c) => s + (c.cuotas || 0), 0);
  const alcance = datos.todas ? "Todas las sucursales" : `Sucursal ${datos.sucursal || "—"}`;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "0.8rem", letterSpacing: "0.08em", color: FP.color.textSecondary, mb: 1.2 }}>
        COBROS DE HOY · {alcance.toUpperCase()}
      </Typography>
      <Card elevation={0} sx={{ borderRadius: 2.5, border: "1px solid #E5E9F0", boxShadow: FP.shadow.soft }}>
        <Box sx={{ display: "flex", gap: { xs: 3, sm: 5 }, flexWrap: "wrap", p: 2, pb: 1.4 }}>
          <Stat valor={clientes.length} etiqueta="Clientes con cuota hoy" />
          <Stat valor={cuotas} etiqueta="Cuotas que vencen" />
          <Stat valor={`$${money(datos.total)}`} etiqueta="Total a cobrar" />
        </Box>

        {visibles.length === 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.6, borderTop: "1px solid #EEF1F6" }}>
            <Icon sx={{ fontSize: 20, color: FP.color.textSecondary }}>task_alt</Icon>
            <Typography sx={{ fontSize: "0.84rem", color: FP.color.textSecondary }}>
              Nadie tiene cuota que venza hoy{datos.todas ? "" : " en tu sucursal"}.
            </Typography>
          </Box>
        )}

        {visibles.length > 0 && (
          <Box sx={{ px: 1, pb: 1, maxHeight: expandido ? 420 : "none", overflowY: "auto" }}>
            {visibles.map((c) => (
              <Box
                key={c.noprest}
                onClick={() => navigate(`/prestamos/expediente/${c.noprest}`)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.4,
                  px: 1,
                  py: 1.1,
                  borderTop: "1px solid #EEF1F6",
                  cursor: "pointer",
                  borderRadius: 1,
                  "&:hover": { bgcolor: `${FP.color.navy}08` },
                }}
              >
                <Icon sx={{ fontSize: 20, color: FP.color.navy, flexShrink: 0 }}>payments</Icon>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontWeight: 700, fontSize: "0.88rem", color: FP.color.textPrimary }}>
                    {String(c.cliente || "").trim() || `Préstamo #${c.noprest}`}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: "0.74rem", color: FP.color.textSecondary }}>
                    Préstamo #{c.noprest}
                    {c.celular || c.telefono ? ` · ${c.celular || c.telefono}` : ""}
                    {datos.todas && c.sucursal ? ` · Suc. ${c.sucursal}` : ""}
                    {c.cuotas > 1 ? ` · ${c.cuotas} cuotas` : ""}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: FP.color.textPrimary, flexShrink: 0 }}>
                  ${money(c.monto)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: "flex", borderTop: "1px solid #EEF1F6" }}>
          {clientes.length > MAX_CLIENTES && (
            <Box
              onClick={() => setExpandido(!expandido)}
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.6,
                py: 1.1,
                borderRight: "1px solid #EEF1F6",
                cursor: "pointer",
                color: FP.color.navy,
                "&:hover": { bgcolor: `${FP.color.navy}08` },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>
                {expandido ? "Mostrar menos" : `Ver los ${clientes.length} clientes`}
              </Typography>
              <Icon sx={{ fontSize: 16 }}>{expandido ? "expand_less" : "expand_more"}</Icon>
            </Box>
          )}
          <Box
            onClick={() => navigate("/pagos/gestion")}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.6,
              py: 1.1,
              cursor: "pointer",
              color: FP.color.navy,
              "&:hover": { bgcolor: `${FP.color.navy}08` },
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "0.8rem" }}>Ir a Cobrar Pagos</Typography>
            <Icon sx={{ fontSize: 16 }}>arrow_forward</Icon>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

export default CobrosDeHoy;
